import { useEffect, useMemo, useRef, useState } from 'react';
import { NJText } from '@engie-group/fluid-design-system-react';
import type { LayerSpecification } from 'mapbox-gl';
import mapboxgl from 'mapbox-gl';
import { getMapboxToken } from '../../urbanmap/_components/UMMap';
import { UMR, UM_MAPBOX_STYLES } from '../../urbanmap/_components/referenceTokens';
import { boundsOf } from './domain';
import { buildingsFC, communesFC, ringFC } from './mapLayers';
import { PC } from './tokens';
import { useDevelopmentPlans } from './plans.hooks';
import { planBuildings, planRoute, routeFC } from './planGeometry';
import { latestDevelopmentPlan } from './plans.data';
import type { LngLat, ProjectDetail, Referential } from './types';

const layers: LayerSpecification[] = [
  { id: 'mini-buildings', type: 'fill', source: 'buildings', paint: { 'fill-color': PC.included, 'fill-opacity': 0.65 } },
  { id: 'mini-area', type: 'fill', source: 'outline', paint: { 'fill-color': PC.outline, 'fill-opacity': 0.08 } },
  { id: 'mini-outline', type: 'line', source: 'outline', paint: { 'line-color': PC.outline, 'line-width': 2 } },
  { id: 'mini-plan-route', type: 'line', source: 'plan-route', paint: { 'line-color': PC.draw, 'line-width': 3 } },
];

export function ProjectMiniMap({ detail, referential }: { detail: ProjectDetail; referential: Referential }) {
  const ref = useRef<HTMLDivElement>(null);
  const token = getMapboxToken();
  const [error, setError] = useState<string>();
  const plans = useDevelopmentPlans(detail.project.id);
  const latest = useMemo(() => latestDevelopmentPlan(plans.data ?? []), [plans.data]);
  const selected = useMemo(() => latest ? planBuildings(latest, detail.buildings) : [], [latest, detail.buildings]);
  const rings = useMemo(() => {
    const perimeter = detail.project.perimeter;
    return perimeter.kind === 'drawn' ? [perimeter.polygon]
      : perimeter.communes.map((id) => referential.communeOutlines[id]).filter((ring): ring is LngLat[] => !!ring);
  }, [detail.project.perimeter, referential.communeOutlines]);
  const bounds = useMemo(() => boundsOf(rings.length ? rings.flat() : detail.buildings.map((building) => building.centroid)), [rings, detail.buildings]);
  const sources = useMemo(() => ({
    buildings: buildingsFC(latest ? selected : detail.buildings, () => ({})),
    'plan-route': routeFC(latest && latest.completedSteps >= 2 ? planRoute(selected) : []),
    outline: detail.project.perimeter.kind === 'communes'
      ? communesFC(detail.project.perimeter.communes, referential.communeGeometries) : ringFC(rings),
  }), [detail.buildings, rings, detail.project.perimeter, referential.communeGeometries, latest, selected]);
  const offlinePaths = useMemo(() => {
    const sw = mapboxgl.MercatorCoordinate.fromLngLat(bounds[0]);
    const ne = mapboxgl.MercatorCoordinate.fromLngLat(bounds[1]);
    const span = Math.max(ne.x - sw.x, sw.y - ne.y);
    const scale = 260 / span;
    const point = (p: number[]) => {
      const m = mapboxgl.MercatorCoordinate.fromLngLat([p[0], p[1]]);
      return `${150 + (m.x - (ne.x + sw.x) / 2) * scale},${150 + (m.y - (ne.y + sw.y) / 2) * scale}`;
    };
    const path = (polygon: number[][][]) => polygon.map((ring) => `M${ring.map(point).join('L')}Z`).join('');
    return {
      buildings: (latest ? selected : detail.buildings).map((building) => building.geometry.type === 'Polygon'
        ? path(building.geometry.coordinates) : building.geometry.coordinates.map(path).join('')),
      outlines: sources.outline.features.flatMap((feature) => feature.geometry.type === 'Polygon'
        ? [path(feature.geometry.coordinates)]
        : feature.geometry.type === 'MultiPolygon' ? feature.geometry.coordinates.map(path) : []),
      routes: sources['plan-route'].features.flatMap((feature) => feature.geometry.type === 'LineString'
        ? [`M${feature.geometry.coordinates.map(point).join('L')}`] : []),
    };
  }, [bounds, detail.buildings, sources, latest, selected]);

  useEffect(() => {
    if (!ref.current || !token) return;
    const map = new mapboxgl.Map({
      container: ref.current,
      accessToken: token || undefined,
      style: UM_MAPBOX_STYLES.find((style) => style.id === 'light')!.style,
      bounds,
      fitBoundsOptions: { padding: 20, maxZoom: 17 },
      interactive: false,
      // These maps are static thumbnails and must retain their pixels in captures.
      preserveDrawingBuffer: true,
      attributionControl: true,
      logoPosition: 'bottom-right',
    });
    const fit = () => { map.resize(); map.fitBounds(bounds, { padding: 20, maxZoom: 17, duration: 0 }); };
    fit();
    map.getCanvas().setAttribute('tabindex', '-1');
    map.on('load', () => {
      Object.entries(sources).forEach(([id, data]) => map.addSource(id, { type: 'geojson', data }));
      layers.forEach((layer) => map.addLayer(layer));
    });
    map.on('error', (event) => setError(event.error.message));
    const resize = new ResizeObserver(fit);
    resize.observe(map.getContainer());
    return () => {
      resize.disconnect();
      map.remove();
    };
  }, [bounds, sources, token]);

  return (
    <div className="pcv3-wf-map" aria-label={latest ? `Carte du dernier plan : ${latest.name}` : `Carte du périmètre de ${detail.project.name}`}>
      <div ref={ref} className="pcv3-map-surface" />
      {!token && (
        <svg className="pcv3-map-surface" viewBox="0 0 300 300" role="img" aria-label={`Bâtiments et contour du périmètre de ${detail.project.name}`}>
          <rect width="300" height="300" fill={UMR.map.fallbackLand} />
          {offlinePaths.buildings.map((d, index) => <path key={index} d={d} fill={PC.included} fillOpacity={0.65} fillRule="evenodd" />)}
          {offlinePaths.outlines.map((d, index) => <path key={index} d={d} fill={PC.outline} fillOpacity={0.08} stroke={PC.outline} strokeWidth={2} fillRule="evenodd" />)}
          {offlinePaths.routes.map((d, index) => <path key={index} d={d} fill="none" stroke={PC.draw} strokeWidth={2} />)}
        </svg>
      )}
      {error ? <NJText role="alert" scale="sm" className="pcv3-map-mode">Erreur de carte : {error}</NJText>
        : plans.isError ? <NJText role="alert" scale="sm" className="pcv3-map-mode">Impossible de charger le dernier plan.</NJText>
          : latest ? <NJText scale="sm" className="pcv3-map-mode">Dernier plan : {latest.name}{latest.completedSteps < 2 ? ' · tracé non défini' : ' · tracé simulé'}</NJText>
            : !token && <NJText scale="sm" className="pcv3-map-mode">Aperçu géographique hors ligne · sans fond Mapbox</NJText>}
    </div>
  );
}
